import { faker } from '@faker-js/faker'
import { AchAccountType, FamilyGroup } from '@getstep/proto/common'
import type { Amount } from '@getstep/proto/common'
import { Status } from '@getstep/proto/profile'
import { amountUSD, HttpClient, randomArrayElement } from '@getstep/sdk'
import { PublicApi } from '@getstep/sdk/dist/http/PublicApi'
import { TestApi } from '@getstep/sdk/dist/http/TestApi'
import { FamilyStore } from '@getstep/sdk/dist/store/FamilyStore'
import { ReferralStore } from '@getstep/sdk/dist/store/ReferralStore'
import { randomRealAddress } from '@getstep/sdk/dist/store/testing'
import { BaseTestApiFactory } from '@getstep/sdk/dist/store/testing/BaseTestApiFactory'
import { randomNumber } from '@getstep/sdk/dist/test'
import { fakeBankLinkRequest } from '@getstep/sdk/dist/test/ExternalAccountFactory'
import {
    externalBankAdhocSimulation,
    externalBankIncomeSimulation,
} from '@getstep/sdk/dist/test/ExternalTransactionSimulationFactory'
import type { TestSdkUser } from '@getstep/sdk/dist/test/TestSdkUser'
import { mustExist } from '@getstep/sdk/dist/util/Assert'
import { splitNames } from '@getstep/sdk/dist/util/Names'
import {
    PhoneNumberType,
    randomNumber as randomNumberRange,
    randomPhoneNumber,
} from '@getstep/sdk/dist/util/Random'
import { addYears, format, subYears } from 'date-fns'
import padStart from 'lodash/padStart'

import { SdkClientStore } from '@lib/store/SdkClientStore'
import type { PageSlug } from '@lib/utils/page'
import { PAGE_OPTIONS } from '@lib/utils/page'
import { LINK } from '@shared/util/link'
import { digitsOnly, validateTelephoneInput } from '@shared/util/phone'

class TestApiFactory extends BaseTestApiFactory {
    get config() {
        return {
            baseURL: process.env.NEXT_PUBLIC_STEP_API_BASE ?? '',
            metadata: {
                appVersion: 'test',
                bundleId: 'test',
                platform: 'web',
            },
        }
    }

    get testApi() {
        return new TestApi(this.config)
    }

    get httpClient() {
        return new HttpClient(this.config)
    }

    get sdk() {
        return SdkClientStore.create()
    }

    get publicApi() {
        return new PublicApi(this.httpClient)
    }

    async generatePhone(
        type:
            | PhoneNumberType.Normal
            | PhoneNumberType.ForceSignup = PhoneNumberType.Normal,
    ): Promise<string> {
        const generate = async () => {
            let attempt = 0
            do {
                const result = randomPhoneNumber(type)
                const isvalid = await validateTelephoneInput(result)
                if (result && isvalid) return result
            } while (attempt++ < 100)
            throw new Error(`Could not generate a valid phone number`)
        }

        const phoneNumber = await generate()
        await testApiFactory.unlinkAlias({
            phoneNumber: await digitsOnly(phoneNumber),
        })
        return phoneNumber
    }

    generateAddress() {
        return randomRealAddress()
    }

    async inviteAdult(adult: { phoneNumber: string }) {
        const teenUser = await this.setupOnboardedTeen()

        await Promise.all([
            testApiFactory.publishProfile(teenUser),
            testApiFactory.addAvatar(teenUser), // Lorem Picsum for random avatars
        ])

        const api = this.api(teenUser)
        const familyStore = new FamilyStore(this.httpClient, api)
        const inviteId = await familyStore.inviteFamilyMember(
            { phoneNumber: await digitsOnly(adult.phoneNumber) },
            FamilyGroup.FAMILY_ADMIN,
        )
        return { teen: teenUser, inviteId }
    }

    generateDateInput() {
        return format(
            // @ts-expect-error - Type inference issue
            subYears(faker.date.recent(undefined, new Date()), 21),
            'MM / dd / yyyy',
        )
    }

    generate18YearOldDateInput() {
        return format(
            // @ts-expect-error - Type compatibility issue
            subYears(faker.date.recent(undefined, new Date()), 18),
            'MM / dd / yyyy',
        )
    }

    generateExpiry() {
        return format(addYears(new Date(), randomNumber(1) || 1), 'MM / yy')
    }

    generateCvc() {
        return `${randomNumber(3)}`
    }

    generateName() {
        const fullName = faker.person.fullName()
        return { fullName, ...splitNames(fullName) }
    }

    async addAvatar(user: TestSdkUser, avatarUrl = '') {
        if (!avatarUrl) return
        return this.api(user).setProfilePicture(avatarUrl)
    }

    async publishProfile(user: TestSdkUser) {
        return this.api(user).setProfile(Status.PUBLIC)
    }

    async sendMoney(amount: Amount) {
        const source = await this.setupKYCedAdult()

        const [phoneNumber] = await Promise.all([
            this.getUnlinkedPhoneNumber(),
            this.loadFunds(source, amount),
            this.addAvatar(source),
            source.loadAccounts(),
        ])

        const phoneNumberDigits = await digitsOnly(phoneNumber)

        return source.transfer.executeSend((builder) => {
            builder
                .transfer(amount)
                .fromAccount(source.spendAccount.depositAccountId)
                .toUser({ phoneNumber: phoneNumberDigits })
        })
    }

    async requestMoney(amount: Amount) {
        const source = await this.setupKYCedAdult()

        const [phoneNumber] = await Promise.all([
            this.getUnlinkedPhoneNumber(),
            this.loadFunds(source, amount),
            this.addAvatar(source),
            source.loadAccounts(),
        ])

        const phoneNumberDigits = await digitsOnly(phoneNumber)

        return source.transfer.request(
            { phoneNumber: phoneNumberDigits },
            source.spendAccount.primaryAccountId,
            amount,
        )
    }

    async createP2PLink(slug: PageSlug<'SEND_MONEY' | 'REQUEST_MONEY'>) {
        if (slug === PAGE_OPTIONS.SEND_MONEY.slug) {
            const transfer = await this.sendMoney(amountUSD(randomNumber(2)))
            return LINK.sendMoney(transfer?.success?.paymentId)
        }

        const transfer = await this.requestMoney(amountUSD(randomNumber(2)))
        return LINK.requestMoney(transfer?.id)
    }

    async createSponsorRequestLink(withApprovalFlow: boolean) {
        const phoneNumber = await this.generatePhone()
        const { inviteId } = await this.inviteAdult({
            phoneNumber,
        })

        return {
            link: LINK.sponsorRequest(withApprovalFlow, inviteId),
            phoneNumber,
        }
    }

    async createReferralCode() {
        const user = await testApiFactory.setupAdult()
        const referral = new ReferralStore(testApiFactory.api(user))
        await referral.load()
        return mustExist(
            referral.code,
            'Referral code expected to be set after ReferralStore.load()',
        )
    }
}

// Prefill types for KYC prefill testing
export enum PrefillType {
    // No prefill will be returned as the Prove TrustScore for the phone number
    // will be too low.
    TRUST_SCORE_TOO_LOW = 'TRUST_SCORE_TOO_LOW',
    // No prefill data will be returned, functionally the same as if the
    // feature is not enabled for the user.
    NO_PREFILL = 'NO_PREFILL',
    // All of these will return randomly generated mock prefill data.
    DOB_EMAIL = 'DOB_EMAIL',
    EMAIL_ONLY = 'EMAIL_ONLY',
    NO_DOB_SSN_OR_EMAIL = 'NO_SSN_DOB_OR_EMAIL',
    DOB_ONLY = 'DOB_ONLY',
    // No email will be returned in the prefill data because it's a
    // verified email already in use by another user.
    DOB_AND_SSN_WITH_VERIFIED_EMAIL = 'SSN_AND_DOB_WITH_VERIFIED_EMAIL',
    // Prove test phone numbers that will be sent to Prove. They will return
    // predefined prefill data if the provided user details match (dob or ssn).
    PROVE = 'PROVE',
}

// Placeholder for Prove prefill fixtures (to be implemented when available)
const ProvePrefillFixtures: Record<string, any> = {
    // Placeholder for Prove test phone numbers
}

// Helper functions for prefill phone number generation
function randomNumbers(length: number): string {
    let result = ''
    for (let i = 0; i < length; i++) {
        result += randomNumberRange({ min: 0, max: 9 }).toString()
    }
    return result
}

function randomTrustScoreSuffix(trustworthy: boolean): string {
    let range
    if (trustworthy) {
        range = { min: 630, max: 999 }
    } else {
        range = { min: 0, max: 629 }
    }
    return padStart(randomNumberRange(range).toString(), 3, '0')
}

/**
 * Generates a phone number for specific prefill testing scenarios
 */
export function prefillPhoneNumber(type: PrefillType): string {
    let phoneNumberPrefix: string
    switch (type) {
        case PrefillType.TRUST_SCORE_TOO_LOW:
            return `111666${randomNumbers(1)}${randomTrustScoreSuffix(false)}`
        case PrefillType.NO_PREFILL:
            return `3${randomNumbers(2)}666${randomNumbers(
                1,
            )}${randomTrustScoreSuffix(true)}`
        case PrefillType.DOB_EMAIL:
            phoneNumberPrefix = '1116661'
            break
        case PrefillType.EMAIL_ONLY:
            phoneNumberPrefix = '1116662'
            break
        case PrefillType.NO_DOB_SSN_OR_EMAIL:
            phoneNumberPrefix = '1116663'
            break
        case PrefillType.DOB_ONLY:
            phoneNumberPrefix = '1116664'
            break
        case PrefillType.DOB_AND_SSN_WITH_VERIFIED_EMAIL:
            phoneNumberPrefix = '1116667'
            break
        case PrefillType.PROVE:
            return randomArrayElement(Object.keys(ProvePrefillFixtures))
    }
    return `${phoneNumberPrefix}${randomTrustScoreSuffix(true)}`
}

/**
 * Generates transaction simulation scenarios that meet borrowing application requirements
 *
 * Requirements:
 * - Credit (income): minHistoryDays=45, minCountTotal=2, minCount30d=1
 * - Debit (spending): minHistoryDays=45, minCountTotal=5, minCount30d=4
 *
 * @returns Array of TransactionSimulationScenario that will pass borrowing requirements
 */
export function borrowingApprovalTransactions() {
    const scenarios = []
    const today = new Date()

    // Add 6 biweekly income transactions (covers 45+ days, provides 2+ total, 1+ in last 30 days)
    // This creates income every 2 weeks for ~12 weeks (84 days)
    scenarios.push(
        externalBankIncomeSimulation({
            amount: amountUSD(500),
            description: 'Payroll',
            occurrences: 6, // 6 payments over 12 weeks
        }),
    )

    // Add 5 debit transactions spread across 45+ days
    // Recent transactions (last 30 days) - 4 transactions
    const daysAgo5 = subYears(today, 0)
    daysAgo5.setDate(today.getDate() - 5)
    scenarios.push(
        externalBankAdhocSimulation({
            amount: amountUSD(-25),
            description: 'Coffee Shop',
            postedDate: {
                year: daysAgo5.getFullYear(),
                month: daysAgo5.getMonth() + 1,
                day: daysAgo5.getDate(),
            },
            stepTransactionDetails: {},
        }),
    )

    const daysAgo10 = subYears(today, 0)
    daysAgo10.setDate(today.getDate() - 10)
    scenarios.push(
        externalBankAdhocSimulation({
            amount: amountUSD(-50),
            description: 'Grocery Store',
            postedDate: {
                year: daysAgo10.getFullYear(),
                month: daysAgo10.getMonth() + 1,
                day: daysAgo10.getDate(),
            },
            stepTransactionDetails: {},
        }),
    )

    const daysAgo15 = subYears(today, 0)
    daysAgo15.setDate(today.getDate() - 15)
    scenarios.push(
        externalBankAdhocSimulation({
            amount: amountUSD(-15),
            description: 'Fast Food',
            postedDate: {
                year: daysAgo15.getFullYear(),
                month: daysAgo15.getMonth() + 1,
                day: daysAgo15.getDate(),
            },
            stepTransactionDetails: {},
        }),
    )

    const daysAgo20 = subYears(today, 0)
    daysAgo20.setDate(today.getDate() - 20)
    scenarios.push(
        externalBankAdhocSimulation({
            amount: amountUSD(-30),
            description: 'Gas Station',
            postedDate: {
                year: daysAgo20.getFullYear(),
                month: daysAgo20.getMonth() + 1,
                day: daysAgo20.getDate(),
            },
            stepTransactionDetails: {},
        }),
    )

    // Older transaction (45+ days ago) - 1 transaction
    const daysAgo50 = subYears(today, 0)
    daysAgo50.setDate(today.getDate() - 50)
    scenarios.push(
        externalBankAdhocSimulation({
            amount: amountUSD(-40),
            description: 'Restaurant',
            postedDate: {
                year: daysAgo50.getFullYear(),
                month: daysAgo50.getMonth() + 1,
                day: daysAgo50.getDate(),
            },
            stepTransactionDetails: {},
        }),
    )

    return scenarios
}

/**
 * Creates a fake bank linking request with optional transactions for borrowing approval
 *
 * @param options - Configuration options
 * @param options.accountHolderName - Account holder name (default: 'Test User')
 * @param options.hasSufficientTransactions - Whether to include transactions that meet borrowing approval requirements (default: true)
 * @param options.accountType - Type of bank account: 'checking' or 'savings' (default: 'checking')
 * @returns LinkingRequest for linking a fake external bank account
 *
 * @example
 * ```typescript
 * import { createFakeBankLinkRequest } from '@shared/util/tests/factory'
 *
 * // With sufficient transactions (default)
 * const linkingRequest = createFakeBankLinkRequest({ accountHolderName: 'John Doe' })
 * await user.api.linkExternalAccount(linkingRequest)
 *
 * // Without sufficient transactions
 * const linkingRequest = createFakeBankLinkRequest({
 *   accountHolderName: 'Jane Doe',
 *   hasSufficientTransactions: false
 * })
 *
 * // Savings account with sufficient transactions
 * const linkingRequest = createFakeBankLinkRequest({
 *   accountHolderName: 'Bob Smith',
 *   accountType: 'savings'
 * })
 * ```
 */
export function createFakeBankLinkRequest(
    options: {
        accountHolderName?: string
        hasSufficientTransactions?: boolean
        accountType?: 'checking' | 'savings'
    } = {},
) {
    const {
        accountHolderName = 'Test User',
        hasSufficientTransactions = true,
        accountType = 'checking',
    } = options

    // Get the base linking request
    const linkingRequest = fakeBankLinkRequest({
        accountHolderName,
        transactionsEnabled: hasSufficientTransactions,
        transactions: hasSufficientTransactions
            ? borrowingApprovalTransactions()
            : [],
        instantTransactionSync: hasSufficientTransactions,
    })

    // Add accountType to the fake property
    if (linkingRequest.fake) {
        linkingRequest.fake.accountType =
            accountType === 'savings'
                ? AchAccountType.SAVINGS
                : AchAccountType.CHECKING
    }

    return linkingRequest
}

/**
 * Don't import this directly in any code except tests!
 * use loadTestApiFactory() instead
 */
export const testApiFactory = new TestApiFactory()
